/**
 * Basic carousel, bringing in a third-party package seemed like overkill
 *
 * Currently only used for displaying reviews (lg size and below, xl and up always show three reviews)
 *
 * This is in its own directory because I had it in mind to modularize it, maybe that will happen
 */

// initial slide to be displayed is second in list of slides with clones
let state = {
  currentSlide: 2,
};

/**
 *
 * @param {Object} updatedProps - state to update
 * @return {Object} - new state
 *
 * Replaces state with new state
 */
function updateState(updatedProps) {
  // note that Sage 9 doesn't understand the spread ... operator so use Object.assign()
  return Object.assign({}, state, updatedProps);
}

/**
 * Get carousel elements
 *
 * Note that if there are ever multiple carousels on a page this may need to change
 *
 * @return {Object} - the DOM elements
 */
function getInitialDomNodes() {
  const slidesContainer = document.querySelector('.carousel-content');
  const initialSlides = document.querySelectorAll('.carousel-slide');

  return {
    initialSlides,
    slidesContainer,
  };
}

/**
 *
 * @param {Object} container - DOM element of carousel container
 * @param {Object} slides - DOM elements of slides
 * @return {Object[]} slidesWithClones - array of slides with clones added to front and back
 *
 * This adds copies of the first and second slides to the beginning of the array slides, and copies of the last and second-to-last to the end
 * Doing this allows for transitions from first -> last and last -> first slides that appear seamless
 */
function addClonedSlides(container, slides) {
  const slidesArray = Array.from(slides);

  const slidesWithClones = [
    slides[slides.length - 2].cloneNode(true),
    slides[slides.length - 1].cloneNode(true),
    ...slidesArray,
    slides[0].cloneNode(true),
    slides[1].cloneNode(true),
  ];

  slidesWithClones.forEach(slide => {
    container.appendChild(slide);
  });

  return slidesWithClones;
}

/**
 *
 * @param {number} length - how many slides are there
 * @param {Object} slidesContainer - DOM element of the slide container
 * @param {function} updateCurrentSlide - change the active slide and what slides are visible
 *
 * Carousel has left/right controls to go forward/back, this adds listeners to theme
 */
function setupSlideControls(length, slidesContainer, updateCurrentSlide) {
  const leftControl = document.querySelector('.carousel-control.left');
  const rightControl = document.querySelector('.carousel-control.right');

  leftControl.addEventListener('click', () =>
    updateCurrentSlide('left', length, slidesContainer)
  );

  rightControl.addEventListener('click', () =>
    updateCurrentSlide('right', length, slidesContainer)
  );
}

/**
 *
 * @param {string} direction - going left or right?
 * @param {number} length - how many slides are there
 * @param {Object} container - DOM element of the slide container
 *
 * Keep track of current slide as state and update accordingly, then trigger CSS transitions to do the actual sliding
 */
function updateCurrentSlide(direction, length, container) {
  // freeze current position so we can check if the slideshow needs to cycle
  const currentCurrent = state.currentSlide;

  // this is what changes to update state
  let currentSlide = state.currentSlide;
  // hidden transition happens when a slide goes from a clone to its original - show the clone, then switch to original with no transition
  let doHidden = false;

  // left or right!?
  switch (direction) {
    case 'left':
      currentSlide = currentSlide - 1;
      doHidden = false;

      state = updateState({ currentSlide });

      // trigger function to run animations and move slides
      updateSlidesContainerPosition(container);

      // at first slide? go to last slide
      if (currentCurrent === 2) {
        // a brief delay means the hidden transition to the last slide will occur after the animated transition to the cloned slide
        setTimeout(() => {
          currentSlide = length - 3;
          doHidden = true;

          state = updateState({ currentSlide });

          updateSlidesContainerPosition(container, doHidden);
        }, 250);
      }

      break;
    case 'right':
      currentSlide = currentSlide + 1;
      doHidden = false;

      state = updateState({ currentSlide });

      updateSlidesContainerPosition(container);

      // at last slide? go to first slide
      if (currentCurrent === length - 3) {
        setTimeout(() => {
          currentSlide = 2;
          doHidden = true;

          state = updateState({ currentSlide });

          updateSlidesContainerPosition(container, doHidden);
        }, 250);
      }

      break;
    default:
      // this can't be directly triggered from the DOM currently but best practice to include it?
      return currentSlide;
  }
}

/**
 *
 * @param {Object} container - DOM element of the slide container, this is what actually is animated
 * @param {boolean} doHidden - animate the transition or not
 *
 * CSS transforms on the slide container are used to animate the slideshow
 */
function updateSlidesContainerPosition(container, doHidden) {
  // different number of slides are visible at different window sizes
  const windowSize = window.innerWidth;

  // if hidde, no time for transform, happens instantly
  if (doHidden) {
    container.style.transition = 'transform 0s ease-in-out';
    // otherwise a brief transition time
  } else {
    container.style.transition = 'transform 0.25s ease-in-out';
  }

  // one slide visible
  if (windowSize <= 767) {
    container.style.transform = `translateX(${state.currentSlide * -100}%)`;
    return;
  }

  // two slides visible
  if (windowSize <= 1279) {
    container.style.transform = `translateX(${state.currentSlide * -50}%)`;
    return;
  }

  // three slides visible
  // not currently used as three reviews are always shown on xl and above sizes
  container.style.transform = `translatex(${(state.currentSlide - 2) *
    -33.333}%)`;
  return;
}

/**
 * Run all the carousel functions wherever the carousel is to appear
 *
 * Currently Home and Reviews page use the carousel to show reviews
 */
function initializeCarousel() {
  const { initialSlides, slidesContainer } = getInitialDomNodes();
  const slidesWithClones = addClonedSlides(slidesContainer, initialSlides);
  const lengthOfSlides = slidesWithClones.length;

  setupSlideControls(lengthOfSlides, slidesContainer, updateCurrentSlide);
}

export default initializeCarousel;



// WEBPACK FOOTER //
// ./scripts/carousel/index.js